/**
 * @fileoverview Canonical alias table for tenant module codes — maps drifted
 *             catalog spellings (kebab-case legacy records, case variants,
 *             and display-name spellings) onto the canonical name-derived
 *             camelCase system module catalog codes.
 * @module sync-config/module-code-aliases
 *
 * Responsibilities:
 * - Define MODULE_CODE_ALIASES: lowercased drifted spelling -> canonical code.
 * - Define canonicalModuleCode: pure trim/lowercase lookup used by every host
 *   that compares tenant `modules[].code` values.
 *
 * Dependencies:
 * - None (pure constants and a pure function).
 *
 * Architecture:
 * - Single source of truth shared by system-api tenant module resolution
 *   (server fallback-only remap) and the web gating layer in core-domain
 *   (`canonicalizeTenantModuleCode`). Per-host copies drift, so every host
 *   imports this table instead of keeping its own.
 * - Canonical codes are the name-derived lowerCamelCase spellings produced by
 *   `moduleCodeFromName` (`wildlandFire`, `fireCodeCompliance`, ...). Kebab
 *   spellings written by retired tooling are the drifted variants.
 *
 * Notes:
 * - Canonical codes and unknown codes pass through unchanged. Each canonical
 *   code's lowercased form is also listed as a self-mapping so case-drifted
 *   stored codes (`'WILDLANDFIRE'`) still resolve to the canonical spelling.
 * - Server callers must only remap when the target exists in the live catalog
 *   (see tenantModuleResolver), so a stored record is never rewritten to a
 *   code the catalog does not contain.
 */

/**
 * @contract MODULE_CODE_ALIASES
 *
 * Purpose:
 * - Lookup from a drifted module code spelling to its canonical catalog code.
 *
 * Fields:
 * - Keys are lowercased, trimmed spellings. Values are canonical catalog codes.
 *
 * Rules:
 * - Keys MUST be lowercase. Lookups normalize input with `toLowerCase()`.
 * - A value MUST be a canonical catalog code, never another alias.
 *
 * Sync:
 * - Shared by api and web hosts via `@firesquirrel/sync-config`.
 */
export const MODULE_CODE_ALIASES: Readonly<Record<string, string>> = {
  wildlandfire: 'wildlandFire',
  wildland: 'wildlandFire',
  'wildland-fire': 'wildlandFire',
  'wildland inspections': 'wildlandFire',
  'wild land': 'wildlandFire',
  firecodecompliance: 'fireCodeCompliance',
  firecode: 'fireCodeCompliance',
  'fire-code': 'fireCodeCompliance',
  'fire-code-compliance': 'fireCodeCompliance',
  'fire code': 'fireCodeCompliance',
  'fire code compliance': 'fireCodeCompliance',
  hydrantmanagement: 'hydrantManagement',
  hydrant: 'hydrantManagement',
  'hydrant-management': 'hydrantManagement',
  'hydrant management': 'hydrantManagement',
  buildingcodecompliance: 'buildingCodeCompliance',
  'building-code-compliance': 'buildingCodeCompliance',
  'building code compliance': 'buildingCodeCompliance',
  permitscertificates: 'permitsCertificates',
  permits: 'permitsCertificates',
  'permits-certificates': 'permitsCertificates',
  'permits & certificates': 'permitsCertificates',
  communityriskreduction: 'communityRiskReduction',
  communityrisk: 'communityRiskReduction',
  'community-risk-reduction': 'communityRiskReduction',
  'community-risk': 'communityRiskReduction',
  'community risk': 'communityRiskReduction',
  'community risk reduction': 'communityRiskReduction',
  customerportal: 'customerPortal',
  custominspectionprograms: 'customInspectionPrograms',
  publiceducation: 'publicEducation',
  tceintegration: 'tceIntegration',
  billing: 'billing',
  advancedai: 'advancedAi',
};

/**
 * @process canonicalModuleCode
 *
 * Steps:
 * 1. Coerce the input to a string and trim surrounding whitespace.
 * 2. Return an empty string for blank or nullish input.
 * 3. Look up the lowercased value in MODULE_CODE_ALIASES.
 * 4. Return the canonical code when found; otherwise return the trimmed input
 *    unchanged.
 *
 * Inputs:
 * - code: a stored or requested module code (any type; non-strings coerce).
 *
 * Outputs:
 * - Canonical code, the trimmed original when no alias applies, or '' for
 *   blank input.
 *
 * Failure Handling:
 * - Never throws; nullish and non-string inputs coerce to ''.
 *
 * Idempotency:
 * - REQUIRED — pure function; canonicalModuleCode(canonicalModuleCode(x)) is
 *   stable because canonical values are never keys.
 */
export function canonicalModuleCode(code: unknown): string {
  const raw = String(code ?? '').trim();
  if (!raw) return '';
  return MODULE_CODE_ALIASES[raw.toLowerCase()] ?? raw;
}
